import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plane,
  Shield,
  Clock,
  MapPin,
  CheckCircle,
  ChevronRight,
  Anchor,
  Fuel,
  Wrench,
} from "lucide-react";

const AviationPage = () => {
  const services = [
    {
      icon: Plane,
      title: "Aircraft Support",
      description: "Comprehensive aircraft support services for government and commercial aviation operations.",
    },
    {
      icon: Anchor,
      title: "Ground Operations",
      description: "Professional ground handling and operations management at airport facilities.",
    },
    {
      icon: Fuel,
      title: "Ferry Coordination",
      description: "Coordination of aircraft ferry flights and repositioning services.",
    },
    {
      icon: Wrench,
      title: "Readiness Services",
      description: "Aviation readiness and preparedness services for rapid deployment missions.",
    },
  ];

  const capabilities = [
    "Aircraft support and maintenance coordination",
    "Ground operations management",
    "Ferry coordination services",
    "Airport-based deployment",
    "Aviation logistics support",
    "Readiness services and preparedness",
    "Airfield operations management",
    "Flight support services",
  ];

  return (
    <div className="min-h-screen" data-testid="aviation-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[60vh] flex items-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1766759465475-b5cf6466aee0?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="aviation-hero"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">
              AVIATION SERVICES
            </p>
            <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">
              AVIATION READINESS & AIRFIELD OPERATIONS
            </h1>
            <p className="text-slate-200 text-lg leading-relaxed mb-8">
              AMR Global Holdings LLC provides comprehensive aviation support from our 
              headquarters at New Orleans Lakefront Airport. Our veteran teams deliver 
              mission-ready aviation services for government and commercial clients.
            </p>
            <div className="flex items-center gap-4 text-white text-sm">
              <MapPin className="w-5 h-5 text-[#c5a04a]" />
              <span>New Orleans Lakefront Airport (KNEW)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="aviation-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CAPABILITIES</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">
              AVIATION SERVICES
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-tactical text-center" data-testid={`aviation-service-${index}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mx-auto mb-6">
                    <service.icon className="w-7 h-7 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-xl text-[#1a365d] mb-3">{service.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities List */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="aviation-capabilities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">FULL SPECTRUM</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                AVIATION CAPABILITIES
              </h2>
              <ul className="space-y-4">
                {capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`capability-${index}`}>
                    <CheckCircle className="w-5 h-5 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#1a365d] p-8 rounded-sm">
                <Clock className="w-10 h-10 text-[#c5a04a] mb-4" />
                <p className="text-4xl font-heading text-white mb-2">72</p>
                <p className="text-slate-300 text-sm tracking-wider">HOUR MOBILIZATION</p>
              </div>
              <div className="bg-[#c5a04a] p-8 rounded-sm">
                <Shield className="w-10 h-10 text-[#1a365d] mb-4" />
                <p className="text-4xl font-heading text-[#1a365d] mb-2">100%</p>
                <p className="text-[#1a365d] text-sm tracking-wider">VETERAN WORKFORCE</p>
              </div>
              <div className="col-span-2 bg-slate-100 p-8 rounded-sm">
                <Plane className="w-10 h-10 text-[#1a365d] mb-4" />
                <p className="text-2xl font-heading text-[#1a365d] mb-2">AIRPORT-BASED</p>
                <p className="text-slate-600 text-sm">Strategic positioning at KNEW for rapid aviation deployment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a365d]" data-testid="aviation-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            AVIATION SUPPORT READY
          </h2>
          <p className="text-slate-200 text-lg mb-8">
            Contact us to discuss your aviation requirements. Our veteran teams 
            are ready to support your mission with military precision.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="aviation-contact-btn">
                Contact Us
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="aviation-services-btn">
                All Services
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AviationPage;
